import { useCallback, useEffect, useMemo, useState } from "react";
import { Roster } from "../components/Roster";
import { TeamCard } from "../components/TeamCard";
import { StateChip } from "../components/bits";
import { api, type WindowTrackedResponse } from "../lib/api";
import { baseEventId, windowState } from "../lib/events";
import { prettyEventName, windowLabel } from "../lib/format";
import { hasImpostor } from "../lib/impostors";
import { Link } from "../lib/router";
import { useApp } from "../lib/store";

const TRACKED_POLL_MS = 5000;

export function WindowPage({ windowId }: { windowId: string }) {
  const { status, grouped, refreshStatus, watchedIds, admin, setRegion } = useApp();
  const [tracked, setTracked] = useState<WindowTrackedResponse | null>(null);
  const [trackedError, setTrackedError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);

  // Locate this window in the catalog for naming + live state. Old
  // windows fall out of the catalog — the page still works from ids.
  const located = useMemo(() => {
    for (const g of grouped ?? []) {
      for (const r of g.regions) {
        const w = r.windows.find(w => w.event_window_id === windowId);
        if (w) return { group: g, region: r.region, eventId: r.event_id, window: w };
      }
    }
    return null;
  }, [grouped, windowId]);

  const watched = status?.windows.find(w => w.event_window_id === windowId);
  const eventId = located?.eventId ?? watched?.event_id ?? "";
  const isLive = located ? windowState(located.window, Date.now()) === "live" : !!watched;

  const idsKey = watchedIds.join(",");
  const loadTracked = useCallback(async () => {
    const ids = idsKey ? idsKey.split(",") : [];
    // No personal watch set → show the empty state, NOT the global set: an
    // absent ?ids falls back to the global set server-side, which would
    // mislabel the operator's curated picks as "My players".
    if (ids.length === 0) {
      setTracked({ event_window_id: windowId, region: "", teams: [] });
      setTrackedError(null);
      return;
    }
    try {
      setTracked(await api.windowTracked(windowId, ids));
      setTrackedError(null);
    } catch (err) {
      setTrackedError(err instanceof Error ? err.message : "failed to load tracked players");
    }
  }, [windowId, idsKey]);

  useEffect(() => {
    setTracked(null);
    loadTracked();
    const t = setInterval(loadTracked, TRACKED_POLL_MS);
    return () => clearInterval(t);
  }, [loadTracked]);

  // Keep the one nav region in step with the window you're actually viewing,
  // so navigating back to home lands you on the right region.
  useEffect(() => {
    const r = located?.region ?? tracked?.region;
    if (r) setRegion(r);
  }, [located?.region, tracked?.region, setRegion]);

  // live duos first, then board order
  const teams = useMemo(() => {
    const list = (tracked?.teams ?? []).filter(t => !hasImpostor(t));
    list.sort((a, b) => {
      if (!!a.live !== !!b.live) return a.live ? -1 : 1;
      return (a.rank ?? 999) - (b.rank ?? 999);
    });
    return list;
  }, [tracked]);

  const run = async (kind: "watch" | "unwatch" | "backfill") => {
    if (busy || !eventId) return;
    setBusy(kind);
    try {
      if (kind === "watch") await api.watchWindow(windowId, eventId);
      if (kind === "unwatch") await api.unwatchWindow(windowId);
      if (kind === "backfill") await api.backfillWindow(windowId, eventId);
      await refreshStatus();
    } catch {
      // nav status chip carries the offline story
    } finally {
      setBusy(null);
    }
  };

  const title = located?.group.title ?? (eventId ? prettyEventName(eventId) : windowId);
  const label = eventId ? windowLabel(windowId, eventId) : "";

  return (
    <main className="mx-auto max-w-6xl px-6 py-8">
      <Link
        to={located ? `/event/${encodeURIComponent(located.group.base_id)}` : "/"}
        className="font-mono text-[11.5px] text-faint transition hover:text-dim"
      >
        ← {located ? located.group.title : eventId ? prettyEventName(baseEventId(eventId)) : "tournaments"}
      </Link>

      <header className="mt-3 flex flex-wrap items-end justify-between gap-4">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {isLive ? (
              <StateChip tone="ok" pulse>
                live
              </StateChip>
            ) : (
              <StateChip tone="dim">{located ? windowState(located.window, Date.now()) : "window"}</StateChip>
            )}
            {tracked?.region && <span className="label-xs">{tracked.region}</span>}
            {admin && watched && (
              <Link
                to={`/window/${encodeURIComponent(windowId)}/debug`}
                className="font-mono text-[11px] text-faint transition-colors hover:text-accent"
              >
                {watched.watching} parsing · {watched.done} captured · {watched.discovered}{" "}
                queued
                {watched.failed > 0 && <span className="text-err"> · {watched.failed} failed</span>}
                {" →"}
              </Link>
            )}
          </div>
          <h1 className="display mt-1.5 text-xl leading-tight text-fg sm:text-2xl">
            {title}
            {label && <span className="text-dim"> · {label}</span>}
          </h1>
        </div>

        {admin && (
          <div className="flex shrink-0 items-center gap-1.5">
            {eventId && watched && (
              <button
                onClick={() => run("unwatch")}
                disabled={busy != null}
                className="press chamfer-sm bg-accent/15 px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-accent transition-colors hover:bg-accent/25"
              >
                {busy === "unwatch" ? "…" : "watching ✓"}
              </button>
            )}
            {eventId && !watched && (
              <button
                onClick={() => run("watch")}
                disabled={busy != null}
                className="press chamfer-sm bg-raised px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-dim transition-colors hover:bg-overlay hover:text-fg"
              >
                {busy === "watch" ? "…" : "watch"}
              </button>
            )}
            {eventId && !isLive && (
              <button
                onClick={() => run("backfill")}
                disabled={busy != null}
                title="parse every match of this window (historical testing)"
                className="press chamfer-sm bg-raised px-3 py-1.5 font-mono text-[11px] font-medium uppercase tracking-wider text-dim transition-colors hover:bg-overlay hover:text-fg"
              >
                {busy === "backfill" ? "…" : "backfill"}
              </button>
            )}
          </div>
        )}
      </header>

      <section className="mt-6">
        <h2 className="display mb-3 flex items-center gap-2 text-[17px] text-fg">
          <span className="pulse-dot h-1.5 w-1.5 rounded-full bg-accent" />
          My players
        </h2>

        {trackedError && (
          <p className="rounded-lg border border-warn/30 bg-warn/[0.07] px-4 py-3 text-xs text-warn">
            {trackedError}
          </p>
        )}
        {!tracked && !trackedError && (
          <p className="font-mono text-[11.5px] text-faint">loading…</p>
        )}
        {tracked &&
          teams.length === 0 &&
          // No watch set at all → guide them to add players. Otherwise none of
          // your pros are on this board yet → still surface the full roster
          // (labelled "pending") so the watch set is always visible/editable.
          (watchedIds.length === 0 ? (
            <p className="rounded-lg bg-raised/60 px-4 py-3 font-mono text-[11.5px] text-faint">
              none of your players are in this window — add players from the home dashboard
            </p>
          ) : (
            <Roster status="pending" />
          ))}

        <div className="grid items-start gap-4 lg:grid-cols-2">
          {teams.map(t => (
            <TeamCard
              key={t.team_id ?? t.members.find(m => m.watched)?.epic_id ?? t.label}
              team={t}
              windowLive={isLive && !!watched}
            />
          ))}
        </div>
      </section>
    </main>
  );
}
